import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  TextField,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Box,
  IconButton,
  CircularProgress,
  Paper,
  Fade,
} from '@mui/material';
import { Upload, X, Instagram } from 'lucide-mui';
import { useTranslation } from 'react-i18next';
import { importUrl } from './importSlice';
import { uploadFiles } from '../upload/uploadSlice';
import ALLOWED_TYPES from '../upload/AllowedTypes';
import useLimits from '../plan/hooks/limits';
import { RootState } from '../../reducers';
import store from '../../store';
import { useBooleanFeature } from '../../hooks/useFeatures';
import { initializeRecorder } from '../recording/recordingSlice';
import { GlobalDialogProps } from '../../types';

const ImportModal: React.FC<GlobalDialogProps> = ({ onClose }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const { teamId, folderId } = useParams<{
    teamId: string;
    folderId: string;
  }>();
  const initialUrl = useSelector(
    (state: RootState) => state.globalModals.actionData.url || ''
  );
  const { library } = useSelector((state: RootState) => state);
  const [url, setUrl] = useState(initialUrl);
  const [error, setError] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const guardLimits = useLimits();
  const { getFeature } = useBooleanFeature();
  const isUploadUnguarded = getFeature('feature_unguard_upload');
  const isImportUrlUnguarded = getFeature('feature_unguard_import_url');
  const [importing, setImporting] = useState(false);
  const [instagramSnackbarOpen, setInstagramSnackbarOpen] = useState(false);
  const [showInstagramNotice, setShowInstagramNotice] = useState(false);

  // Helper function to check if URL is Instagram
  const isInstagramUrl = (urlToCheck: string): boolean => {
    const lowercaseUrl = urlToCheck.toLowerCase();
    return (
      lowercaseUrl.includes('instagram.com/p/') ||
      lowercaseUrl.includes('instagram.com/reel/') ||
      lowercaseUrl.includes('instagram.com/tv/')
    );
  };

  // Check for Instagram URL when URL changes
  useEffect(() => {
    const trimmedUrl = url.trim();
    if (trimmedUrl && isInstagramUrl(trimmedUrl)) {
      setShowInstagramNotice(true);
    } else {
      setShowInstagramNotice(false);
    }
  }, [url]);

  // Conditional guard function that respects the feature flag for upload-related actions
  const conditionalUploadGuard = React.useCallback(
    (limitType: string, callback: () => void) => {
      if (isUploadUnguarded && limitType === 'importModalUploadButton') {
        // If upload is unguarded and this is an upload action, execute callback directly
        callback();
      } else {
        // Use normal guard limits
        guardLimits(limitType as any, callback);
      }
    },
    [isUploadUnguarded, guardLimits]
  );

  // Conditional guard function that respects the feature flag for import URL actions
  const conditionalImportGuard = React.useCallback(
    (limitType: string, callback: () => void) => {
      if (isImportUrlUnguarded && limitType === 'importUrlButton') {
        // If import URL is unguarded and this is an import URL action, execute callback directly
        callback();
      } else {
        // Use normal guard limits
        guardLimits(limitType as any, callback);
      }
    },
    [isImportUrlUnguarded, guardLimits]
  );

  useEffect(() => {
    if (initialUrl) {
      setUrl(initialUrl);
    }
  }, [initialUrl]);

  const handleImport = async () => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      setOpenSnackbar(true);
      return;
    }

    if (!teamId || !folderId) {
      setOpenSnackbar(true);
      return;
    }

    // Check if it's an Instagram URL
    if (isInstagramUrl(trimmedUrl)) {
      // Open Instagram URL in new tab
      window.open(trimmedUrl, '_blank');

      // Initialize screen recorder with browser audio only, no microphone, no video
      const settings = {
        audioSettings: { microphoneAudio: false, browserAudio: true },
        videoSettings: { screen: true, webCam: false },
      };

      dispatch(initializeRecorder({ teamId, folderId, settings }));

      // Show snackbar with instructions
      setInstagramSnackbarOpen(true);

      // Close the modal
      onClose();
      return;
    }

    setImporting(true);
    conditionalImportGuard('importUrlButton', async () => {
      try {
        // Dispatch importUrl and wait for the result
        await dispatch(importUrl({ teamId, folderId, url: trimmedUrl }));

        // Get initial timestamp to detect refresh
        const initialRefreshTimestamp = library.lastRefreshCompleted;

        // Check if the grid has refreshed by comparing timestamps
        const waitForGridRefresh = () => {
          return new Promise<void>((resolve) => {
            // Maximum time to wait (3 seconds)
            const maxWaitTime = 4000;
            const startTime = Date.now();

            const checkRefresh = () => {
              const currentState = store.getState();
              const currentTimestamp =
                currentState.library.lastRefreshCompleted;
              const importStatus = currentState.import.status;

              // If there's a failure, resolve immediately
              if (importStatus === 'failed') {
                resolve();
                return;
              }

              // If the timestamp has changed or max time reached, resolve
              if (
                currentTimestamp > initialRefreshTimestamp ||
                Date.now() - startTime > maxWaitTime
              ) {
                resolve();
              } else {
                // Check again in 100ms
                setTimeout(checkRefresh, 100);
              }
            };

            // Start checking
            checkRefresh();
          });
        };

        // Wait for the grid to refresh before closing
        await waitForGridRefresh();

        setImporting(false);
        // Now close the modal
        onClose();
      } catch (importError) {
        // If there's an error, close the modal immediately as this indicates an API failure
        console.error('Import error:', importError);
        setImporting(false);
        onClose();
      }
    });
  };

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(event.target.value);
    // Clear errors when user starts typing again
    if (error) {
      setError('');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !importing && url.trim()) {
      event.preventDefault();
      handleImport();
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleCloseInstagramSnackbar = () => {
    setInstagramSnackbarOpen(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const { files } = event.dataTransfer;
    if (teamId && folderId && files) {
      const filesArray = Array.from(files as ArrayLike<File>);
      const filteredFiles = filesArray.filter((file) =>
        ALLOWED_TYPES.includes(file.type)
      );
      dispatch(uploadFiles({ teamId, folderId, files: filteredFiles }));
      onClose();
    }
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (teamId && folderId && event.target.files) {
      const filesArray = Array.from(event.target.files as ArrayLike<File>);
      const filteredFiles = filesArray.filter((file) =>
        ALLOWED_TYPES.includes(file.type)
      );
      dispatch(uploadFiles({ teamId, folderId, files: filteredFiles }));
      onClose();
    }
  };

  const handleUploadClick = () => {
    conditionalUploadGuard('importModalUploadButton', () => {
      document.getElementById('file-input')?.click();
    });
  };

  const handleDirectImport = async () => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl || !teamId || !folderId) {
      setOpenSnackbar(true);
      return;
    }

    setImporting(true);
    conditionalImportGuard('importUrlButton', async () => {
      try {
        // Dispatch importUrl and wait for the result
        await dispatch(importUrl({ teamId, folderId, url: trimmedUrl }));

        // Get initial timestamp to detect refresh
        const initialRefreshTimestamp = library.lastRefreshCompleted;

        // Check if the grid has refreshed by comparing timestamps
        const waitForGridRefresh = () => {
          return new Promise<void>((resolve) => {
            // Maximum time to wait (3 seconds)
            const maxWaitTime = 4000;
            const startTime = Date.now();

            const checkRefresh = () => {
              const currentState = store.getState();
              const currentTimestamp =
                currentState.library.lastRefreshCompleted;
              const importStatus = currentState.import.status;

              // If there's a failure, resolve immediately
              if (importStatus === 'failed') {
                resolve();
                return;
              }

              // If the timestamp has changed or max time reached, resolve
              if (
                currentTimestamp > initialRefreshTimestamp ||
                Date.now() - startTime > maxWaitTime
              ) {
                resolve();
              } else {
                // Check again in 100ms
                setTimeout(checkRefresh, 100);
              }
            };

            // Start checking
            checkRefresh();
          });
        };

        // Wait for the grid to refresh before closing
        await waitForGridRefresh();

        setImporting(false);
        // Now close the modal
        onClose();
      } catch (importError) {
        // If there's an error, close the modal immediately as this indicates an API failure
        console.error('Import error:', importError);
        setImporting(false);
        onClose();
      }
    });
  };

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        maxWidth="sm"
        fullWidth={isSmallScreen}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            width: isSmallScreen ? '100%' : '32rem',
            margin: isSmallScreen ? theme.spacing(2) : undefined,
            position: 'relative',
            bgcolor: theme.palette.background.paper,
          },
        }}
      >
        <IconButton
          aria-label={t('dialog.close')}
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: isSmallScreen ? 12 : 16,
            top: isSmallScreen ? 12 : 16,
            color: theme.palette.text.secondary,
            padding: isSmallScreen ? '8px' : '6px',
            zIndex: 1,
            '&:active': {
              transform: 'translateY(1px)',
            },
          }}
        >
          <X sx={{ width: 20, height: 20 }} />
        </IconButton>

        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3),
            padding: theme.spacing(4, 3, 3),
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontWeight: 500,
                mb: 1,
                color: theme.palette.text.primary,
              }}
            >
              {t('import.modal.title')}
            </Typography>
            <Typography
              variant="body1"
              color="textSecondary"
              sx={{ maxWidth: '440px', mx: 'auto' }}
            >
              {t('import.modal.description')}
            </Typography>
          </Box>

          <TextField
            label={t('import.modal.url.label')}
            variant="outlined"
            value={url}
            onChange={handleUrlChange}
            onKeyPress={handleKeyPress}
            placeholder={t('import.modal.url.placeholder')}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              mt: -1.5,
              display: 'block',
              fontSize: '0.75rem',
              fontStyle: 'italic',
            }}
          >
            {t('import.modal.url.note')}
          </Typography>

          {showInstagramNotice && (
            <Fade in={showInstagramNotice} timeout={500}>
              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  py: 2,
                  px: 3,
                  borderRadius: 2,
                  backgroundColor: `${theme.palette.info.main}15`,
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '8px',
                      background:
                        'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Instagram
                      sx={{
                        width: 20,
                        height: 20,
                        color: 'white',
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        mb: 0.25,
                      }}
                    >
                      {t('import.modal.instagram.title')}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        display: 'block',
                      }}
                    >
                      {t('import.modal.instagram.description')}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Fade>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={handleImport}
            disabled={!teamId || !folderId || importing}
            fullWidth
            size="large"
            sx={{
              py: 1.5,
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
              },
              position: 'relative',
            }}
          >
            {importing ? (
              <>
                <CircularProgress
                  size={24}
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    marginLeft: '-12px',
                  }}
                />
                <Box sx={{ opacity: 0 }}>{t('import.modal.importing')}</Box>
              </>
            ) : (
              <>
                {showInstagramNotice
                  ? t('import.modal.instagram.button')
                  : t('actions.menu.importUrl')}
              </>
            )}
          </Button>

          {showInstagramNotice && (
            <Box sx={{ textAlign: 'center', mt: 1.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setShowInstagramNotice(false);
                  // Proceed with normal import
                  handleDirectImport();
                }}
                sx={{
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  px: 1.5,
                  py: 0.5,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    borderColor: theme.palette.text.secondary,
                    bgcolor: theme.palette.action.hover,
                    color: theme.palette.text.primary,
                  },
                }}
              >
                {t('import.modal.instagram.directImport')}
              </Button>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.disabled,
                  fontSize: '0.65rem',
                  fontStyle: 'italic',
                  mt: 0.5,
                  display: 'block',
                }}
              >
                {t('import.modal.instagram.directImportNote')}
              </Typography>
            </Box>
          )}

          <Box sx={{ textAlign: 'center', my: 1 }}>
            <Typography variant="body2" color="textSecondary">
              {t('import.modal.upload.or')}
            </Typography>
          </Box>

          <Box
            sx={{
              width: '100%',
              minHeight: '180px',
              border: '3px dashed',
              borderColor: isDragging
                ? theme.palette.primary.main
                : theme.palette.secondary.main,
              borderRadius: '12px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              backgroundColor: isDragging
                ? theme.palette.action.hover
                : 'transparent',
              transform: isDragging ? 'scale(1.02)' : 'scale(1)',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.action.hover,
                '& .MuiSvgIcon-root': {
                  transform: 'translateY(-3px)',
                },
              },
            }}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onClick={handleUploadClick}
          >
            <Upload
              sx={{
                fontSize: '3.5rem',
                color: isDragging
                  ? theme.palette.primary.main
                  : theme.palette.secondary.dark,
                mb: 2,
                transition: 'all 0.2s ease-in-out',
                transform: isDragging ? 'translateY(-5px)' : 'none',
              }}
            />
            <Typography
              variant="body1"
              sx={{
                color: isDragging
                  ? theme.palette.primary.main
                  : theme.palette.text.primary,
                fontWeight: isDragging ? 500 : 400,
                mb: 2,
              }}
            >
              {isDragging
                ? t('import.modal.upload.dragDrop.dragging')
                : t('import.modal.upload.dragDrop.idle')}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              sx={{
                px: 3,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                },
              }}
              data-testid="upload-file-button-upload-modal"
            >
              {t('import.modal.upload.button')}
            </Button>
          </Box>
          <input
            id="file-input"
            data-testid="file-input"
            type="file"
            multiple
            accept={ALLOWED_TYPES.join(',')}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: '100%' }}
        >
          {t('import.modal.url.error')}
        </Alert>
      </Snackbar>

      <Snackbar
        open={instagramSnackbarOpen}
        autoHideDuration={10000}
        onClose={handleCloseInstagramSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseInstagramSnackbar}
          severity="info"
          sx={{ width: '100%' }}
        >
          {t('import.modal.instagram.alert')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ImportModal;
